// components/messages/ContactMessageReply.tsx
import React, { useState } from 'react';
import { ContactMessageService } from '../../services/contactMessageService';
import { showToast } from '../../utils/toast';
import './ContactMessageReply.css';

interface ContactMessageReplyProps {
    contactMessageId: string;
    onReplySuccess?: () => void;
}

/**
 * Reply component for contact form messages
 * Only shown for contact_form message types
 */
const ContactMessageReply: React.FC<ContactMessageReplyProps> = ({
    contactMessageId,
    onReplySuccess
}) => {
    const [isReplying, setIsReplying] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleReplyClick = () => {
        setIsReplying(true);
    };

    const handleCancel = () => {
        setIsReplying(false);
        setReplyText('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!replyText.trim()) {
            showToast('Please enter a reply message', 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            const result = await ContactMessageService.replyToContactMessage(
                contactMessageId,
                replyText
            );

            if (result.success) {
                showToast('Reply sent successfully!', 'success');
                setReplyText('');
                setIsReplying(false);
                if (onReplySuccess) {
                    onReplySuccess();
                }
            } else {
                showToast(result.error || 'Failed to send reply', 'error');
            }
        } catch (error) {
            console.error('Error sending reply:', error);
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isReplying) {
        return (
            <div className="contact-message-reply-trigger">
                <button
                    className="btn btn-primary btn-reply"
                    onClick={handleReplyClick}
                >
                    <i className="bi bi-reply"></i>
                    Reply to Contact Message
                </button>
            </div>
        );
    }

    return (
        <div className="contact-message-reply-form">
            <div className="reply-form-header">
                <h4>
                    <i className="bi bi-reply"></i>
                    Reply to Contact Message
                </h4>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="form-group">
                    <label htmlFor="reply-text">Your Reply</label>
                    <textarea
                        id="reply-text"
                        className="form-control"
                        rows={6}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Type your reply here..."
                        disabled={isSubmitting}
                        required
                    />
                    <small className="form-help">
                        This reply will be sent as a website message to the person who submitted the contact form.
                    </small>
                </div>

                <div className="reply-form-actions">
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <i className="bi bi-hourglass-split"></i>
                                Sending...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-send"></i>
                                Send Reply
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ContactMessageReply;
